import { SharedState } from "@/common/state";

/**
 * Woopra event for admin invitations.
 *
 * Mirrors the transport used by report-analytics.ts: identify the actor, track
 * the event, flush. Woopra is loaded by the snippet in public/index.html, so
 * `window.woopra` is absent in tests and in any environment where the script
 * did not load — every call is optional and wrapped, because analytics must
 * never be able to break the action it is reporting on.
 */
// `Window.woopra` is already declared globally by report-analytics.ts;
// redeclaring it here with a different signature is a type conflict, so this
// imports that module's ambient declaration by referencing it.
import "@/views/reports/data/report-analytics";

const compact = (o: Record<string, unknown>) =>
  Object.entries(o).reduce((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== "") {
      acc[k] = v;
    }
    return acc;
  }, {} as Record<string, unknown>);

export interface AdminInvitePayload {
  /** the addresses actually submitted */
  emails: string[];
  /** the role id chosen in the dialog ("system" | "business") */
  role_id: string;
  /** its label, so the event is readable without decoding ids */
  role_name: string;
}

/**
 * Sent when the user confirms the Invite Admins dialog.
 *
 * Deliberately called only on a successful submit: cancelling or failing
 * validation must not produce an event, so this is not wired to the button but
 * to the branch that actually invites.
 */
export const trackAdminInvite = (payload: AdminInvitePayload) => {
  try {
    const user = SharedState.loggedUser.value;
    const env = SharedState.env.value;
    // LoggedUser calls the organization `orgName`; there is no `company` field.
    // Typed access rather than `as any`, so a wrong name fails the build
    // instead of silently sending an event with the organization missing.
    const actorEmail = user && user.email;
    const company = user && user.orgName;

    window.woopra?.identify(
      compact({
        email: actorEmail,
        EmailAddress: actorEmail,
        company,
        organization: company,
        partner_id: user && user.partnerId,
        environment: env && env.env,
      })
    );
    window.woopra?.track(
      "admin_invite",
      compact({
        // the ticket asks for all three: who was invited, as what, and how many
        invited_emails: payload.emails.join(", "),
        invited_count: payload.emails.length,
        admin_role: payload.role_name,
        admin_role_id: payload.role_id,
        organization: company,
        partner_id: user && user.partnerId,
        environment: env && env.env,
        context: location.pathname,
      })
    );
    window.woopra?.update();
  } catch (_) {
    /* ignore Woopra client errors */
  }
};
